/* altitude-variant-gallery.js — per-variant PDP gallery filter.
 *
 * ct-1786988141 (2026-08-17). The client's Final Image Delivery maps every image to a specific
 * SKU, but umoni shows ALL product media to every variant: wpbingo's showVariantMedia() only
 * slides to variant.featured_media, it never filters. After loading the full approved set that
 * meant Tritour showed all 51 images (and a 39,357px-tall desktop page) no matter which finish
 * you picked. This restores the client's intent: pick a finish, see that finish's images.
 *
 * Data: product metafield custom.variant_media_map, emitted into the page by layout/theme.liquid
 * as #af-variant-media-map -> { "<variantId>": { sku, media:[<mediaId>,...] } }.
 * Media not listed for ANY variant (e.g. the superseded originals still on the product) are
 * hidden by the same pass, which is deliberate.
 *
 * Two different mechanisms behind the same markup, both handled:
 *   <=991px  .js-product-media-group and .js-product-thumbnails are live Swiper instances
 *            (el.swiper) — hidden slides need an explicit .update() or the geometry is stale.
 *   desktop  neither is a Swiper; the wrapper is a plain flex-wrap vertical stack, so
 *            display:none is enough on its own.
 *
 * Variant detection polls the form's [name="id"] input rather than binding wpbingo's jQuery
 * "variantChange" event: the poll cannot be broken by a theme update renaming or renamespacing
 * that event, and it also covers the initial ?variant= deep link.
 */
(function () {
  'use strict';

  var el = document.getElementById('af-variant-media-map');
  if (!el) return;

  var MAP;
  try { MAP = JSON.parse(el.textContent || '{}'); } catch (e) { return; }
  if (!MAP || !Object.keys(MAP).length) return;

  var GROUPS = ['.js-product-media-group', '.js-product-thumbnails'];
  var last = null;

  function mediaIdOf(slide) {
    // the id lives on a descendant as "<sectionId>-<mediaId>"; the section id itself
    // contains hyphens, so take the trailing numeric segment only.
    var node = slide.querySelector('[data-media-id]');
    var raw = (node && node.getAttribute('data-media-id')) || slide.getAttribute('data-media-id') || '';
    var tail = raw.split('-').pop();
    return /^\d+$/.test(tail) ? tail : null;
  }

  function apply(variantId) {
    var entry = MAP[String(variantId)];
    if (!entry || !entry.media || !entry.media.length) return false;

    var keep = Object.create(null);
    for (var i = 0; i < entry.media.length; i++) keep[String(entry.media[i])] = true;

    var shownAnywhere = 0;
    GROUPS.forEach(function (sel) {
      var root = document.querySelector(sel);
      if (!root) return;
      var wrapper = root.querySelector('.swiper-wrapper') || root;
      var slides = [].slice.call(root.querySelectorAll('.swiper-slide'));

      var shown = [], hidden = [], byId = {};
      for (var i = 0; i < slides.length; i++) {
        var id = mediaIdOf(slides[i]);
        // never hide a slide we cannot identify — an unreadable id must not blank the gallery
        var show = id === null ? true : !!keep[id];
        slides[i].style.display = show ? '' : 'none';
        slides[i].setAttribute('aria-hidden', show ? 'false' : 'true');
        if (show) { shown.push(slides[i]); if (id && !byId[id]) byId[id] = slides[i]; }
        else hidden.push(slides[i]);
      }
      shownAnywhere += shown.length;

      // Hiding alone is not enough: the slides keep the PRODUCT gallery's order, and an image
      // shared with an earlier variant sits earlier in that order than this variant's own hero.
      // On Sakai that put the shared "Next-Gen DC Motor" graphic (gallery position 6) ahead of
      // the Matte Black / Dark Brown / Aged Bronze hero (position 19), so the variant opened on
      // an infographic instead of the fan. Re-seat the visible slides into the map's order —
      // hero first — and push the hidden ones behind them so index 0 is always the hero.
      var ordered = [];
      for (var k = 0; k < entry.media.length; k++) {
        var s = byId[String(entry.media[k])];
        if (s && ordered.indexOf(s) === -1) ordered.push(s);
      }
      for (var j = 0; j < shown.length; j++) if (ordered.indexOf(shown[j]) === -1) ordered.push(shown[j]);
      for (var a = 0; a < ordered.length; a++) wrapper.appendChild(ordered[a]);
      for (var h = 0; h < hidden.length; h++) wrapper.appendChild(hidden[h]);

      // Swiper caches its slide list and positions by DOM index, so the move above is only
      // real to it after an update(). Desktop is a plain flex stack and needs nothing.
      if (root.swiper && typeof root.swiper.update === 'function') {
        try { root.swiper.update(); root.swiper.slideTo(0, 0); } catch (e) { /* geometry only */ }
      }
    });

    // If the map and the DOM ever disagree completely, show everything again rather than
    // leave the customer looking at an empty gallery.
    if (!shownAnywhere) {
      GROUPS.forEach(function (sel) {
        var root = document.querySelector(sel);
        if (!root) return;
        var slides = root.querySelectorAll('.swiper-slide');
        for (var i = 0; i < slides.length; i++) {
          slides[i].style.display = '';
          slides[i].setAttribute('aria-hidden', 'false');
        }
      });
      return false;
    }
    return true;
  }

  // A PDP carries SEVERAL form[action*="/cart/add"] [name="id"] inputs — Related Products and
  // Recently Viewed each render their own add-to-cart form — so "the first one in the DOM" is
  // the main product only by luck of ordering. Every candidate is therefore validated against
  // MAP, which contains this product's variants and nothing else: a neighbouring card's variant
  // id simply is not a key, so it can never be mistaken for the selection.
  function currentVariantId() {
    var keys = Object.keys(MAP);
    var inputs = document.querySelectorAll('form[action*="/cart/add"] [name="id"]');
    for (var i = 0; i < inputs.length; i++) {
      var v = String(inputs[i].value || '');
      if (v && MAP[v]) return v;
    }
    var m = window.location.search.match(/[?&]variant=(\d+)/);
    if (m && MAP[m[1]]) return m[1];
    // A sold-out product renders its variant input empty (Farmdale). With one variant there is
    // nothing to disambiguate, so filter to it rather than leaving the gallery unfiltered.
    if (keys.length === 1) return keys[0];
    return null;
  }

  function tick() {
    var id = currentVariantId();
    if (id && id !== last) { if (apply(id)) last = id; }
  }

  function start() {
    tick();
    setInterval(tick, 300);
    // the gallery markup is re-rendered by the theme on some variant changes
    window.addEventListener('pageshow', tick);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
